import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { SearchResult, PaginatedResponse, ObjectType } from '../api/types';
import { Pagination } from '../components/Pagination';

const PAGE_SIZE = 25;

export function Search() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [objectTypes, setObjectTypes] = useState<ObjectType[]>([]);
  const [selectedType, setSelectedType] = useState(searchParams.get('objectType') || '');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [results, setResults] = useState<PaginatedResponse<SearchResult> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load object types on mount
  useEffect(() => {
    api.get<ObjectType[]>('/objects')
      .then(setObjectTypes)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load backup objects'));
  }, []);

  // Run search when params change
  useEffect(() => {
    const q = searchParams.get('q');
    const objectType = searchParams.get('objectType');
    const p = Number(searchParams.get('page')) || 1;

    if (!objectType) {
      setResults(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    api.post<PaginatedResponse<SearchResult>>('/search', {
      orgId: undefined, // uses default org
      objectName: objectType || undefined,
      query: q || undefined,
      page: p,
      pageSize: PAGE_SIZE,
    })
      .then((data) => {
        if (!cancelled) {
          setResults(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Search failed');
          setResults(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [searchParams]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (query) params.q = query;
    if (selectedType) params.objectType = selectedType;
    params.page = '1';
    setPage(1);
    setSearchParams(params);
  }

  function handlePageChange(newPage: number) {
    setPage(newPage);
    const params: Record<string, string> = {};
    if (query) params.q = query;
    if (selectedType) params.objectType = selectedType;
    params.page = String(newPage);
    setSearchParams(params);
  }

  function openRecord(result: SearchResult) {
    navigate(`/records/${result.objectType}/${result.recordId}`);
  }

  return (
    <div>
      <h2>Search Records</h2>

      {/* Search Form */}
      <form
        onSubmit={handleSubmit}
        className="card"
        role="search"
        aria-label="Search backup records"
        style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
      >
        <div className="form-group" style={{ flex: '0 0 200px', marginBottom: 0 }}>
          <label htmlFor="objectType">Object Type</label>
          <select
            id="objectType"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="">Select an object…</option>
            {objectTypes.map((ot) => (
              <option key={ot.apiName} value={ot.apiName}>
                {ot.label} ({ot.recordCount.toLocaleString()})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
          <label htmlFor="searchQuery">Search</label>
          <input
            id="searchQuery"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Record name or ID…"
            autoComplete="off"
          />
        </div>

        <button type="submit" disabled={isLoading || !selectedType} style={{ height: '36px' }}>
          {isLoading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="card" role="alert" style={{ borderLeft: '4px solid var(--color-error)', marginTop: '1rem' }}>
          <p style={{ color: 'var(--color-error)', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Results */}
      {results && (
        <div style={{ marginTop: '1.5rem' }}>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
            {results.totalCount.toLocaleString()} result{results.totalCount !== 1 ? 's' : ''} found
          </p>

          {results.items.length > 0 ? (
            <>
              <table aria-label="Search results">
                <thead>
                  <tr>
                    <th scope="col">Object Type</th>
                    <th scope="col">Record ID</th>
                    <th scope="col">Name</th>
                    <th scope="col">Last Modified</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.items.map((item) => (
                    <tr
                      key={item.recordId}
                      onClick={() => openRecord(item)}
                      style={{ cursor: 'pointer' }}
                      tabIndex={0}
                      role="link"
                      aria-label={`View record ${item.name}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openRecord(item);
                        }
                      }}
                    >
                      <td>
                        <span className="badge badge-info">{item.objectType}</span>
                      </td>
                      <td>
                        <code style={{ fontSize: '0.8rem' }}>{item.recordId}</code>
                      </td>
                      <td>{item.name}</td>
                      <td>{new Date(item.lastModified).toLocaleString()}</td>
                      <td>
                        {item.isDeleted ? (
                          <span className="badge badge-error">DELETED</span>
                        ) : (
                          <span className="badge badge-success">ACTIVE</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={results.totalCount}
                onPageChange={handlePageChange}
              />
            </>
          ) : (
            <p>No records match your search criteria.</p>
          )}
        </div>
      )}
    </div>
  );
}
